"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export interface UseRotatingPanelOptions {
  count: number;
  intervalMs?: number;
  paused?: boolean;
  /** Disables auto-rotation entirely; panels still switch on demand. */
  disabled?: boolean;
}

export interface RotatingPanelState {
  active: number;
  /** Elapsed share of the current interval, 0..1. */
  progress: number;
  setActive: (index: number) => void;
  next: () => void;
  prev: () => void;
}

export function useRotatingPanel({
  count,
  intervalMs = 4500,
  paused = false,
  disabled = false,
}: UseRotatingPanelOptions): RotatingPanelState {
  const [active, setActiveState] = useState(0);
  const [progress, setProgress] = useState(0);
  const [hidden, setHidden] = useState(false);
  const activeRef = useRef(0);
  const elapsedRef = useRef(0);

  useEffect(() => {
    const update = () => setHidden(document.visibilityState === "hidden");
    update();
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  useEffect(() => {
    if (disabled || paused || hidden) return;

    let frame = 0;
    let last: number | null = null;

    const tick = (now: number) => {
      if (last !== null) elapsedRef.current += now - last;
      last = now;

      if (elapsedRef.current >= intervalMs) {
        elapsedRef.current = 0;
        activeRef.current = (activeRef.current + 1) % count;
        setActiveState(activeRef.current);
      }

      setProgress(elapsedRef.current / intervalMs);
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [disabled, paused, hidden, intervalMs, count]);

  const setActive = useCallback(
    (index: number) => {
      const normalized = ((index % count) + count) % count;
      activeRef.current = normalized;
      elapsedRef.current = 0;
      setActiveState(normalized);
      setProgress(0);
    },
    [count],
  );

  const next = useCallback(() => setActive(activeRef.current + 1), [setActive]);
  const prev = useCallback(() => setActive(activeRef.current - 1), [setActive]);

  return { active, progress, setActive, next, prev };
}
